import React from 'react'
import {
  AdminPanelSettingsOutlined,
  AssessmentOutlined,
  ArrowForward,
  DescriptionOutlined,
  PeopleOutline,
  SmartToyOutlined,
} from '@mui/icons-material'
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  Typography,
} from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'

const POPULI_MODULES = [
  {
    title: 'Sayfa Kullanıcıları',
    description: 'Uygulamaya erişecek hesapları oluşturun, rollerini yönetin veya erişimden çıkarın.',
    buttonLabel: 'Kullanıcı Yönetimini Aç',
    path: '/admin-users',
    icon: AdminPanelSettingsOutlined,
    color: '#c05621',
    background: '#fff5eb',
    adminOnly: true,
  },
  {
    title: 'Doküman Yönetimi',
    description: 'Populi bilgi tabanındaki klasörleri ve dokümanları yönetin.',
    buttonLabel: 'Dokümanlara Git',
    path: '/populi/documents',
    icon: DescriptionOutlined,
    color: '#347fb1',
    background: '#edf6fc',
  },
  {
    title: 'Populi Raporları',
    description: 'Kullanım istatistiklerini, konuşmaları ve kaynak dağılımlarını inceleyin.',
    buttonLabel: 'Raporları Aç',
    path: '/populi/reports',
    icon: AssessmentOutlined,
    color: '#27946d',
    background: '#edf9f5',
  },
  {
    title: 'Kullanıcı Yönetimi',
    description: 'Populi kullanıcılarını, rollerini ve konuşma geçmişlerini yönetin.',
    buttonLabel: 'Kullanıcıları Aç',
    path: '/populi/users',
    icon: PeopleOutline,
    color: '#805ad5',
    background: '#f4f0fc',
  },
  {
    title: 'Populi Bot Demo',
    description: 'Farklı erişim rolleriyle Populi sohbet deneyimini test edin.',
    buttonLabel: 'Demo’yu Başlat',
    path: '/populi/demo',
    icon: SmartToyOutlined,
    color: '#d99a18',
    background: '#fff8e8',
  },
]

export default function DashboardPage() {
  const navigate = useNavigate()
  const { userRole } = useAuth()
  const visibleModules = POPULI_MODULES.filter((module) => !module.adminOnly || userRole === 'admin')

  return (
    <Box
      component="main"
      sx={{
        minHeight: 'calc(100vh - 64px)',
        boxSizing: 'border-box',
        px: { xs: 2, sm: 3 },
        py: { xs: 2.5, md: 3.5 },
        background: 'linear-gradient(180deg, #f7fafc 0%, #eef4f7 100%)',
      }}
    >
      <Box sx={{ width: '100%', maxWidth: 1040, mx: 'auto' }}>
        <Stack spacing={1} sx={{ mb: { xs: 2.5, md: 3 } }}>
          <Chip
            icon={<SmartToyOutlined />}
            label="Populi"
            size="small"
            sx={{
              alignSelf: 'flex-start',
              color: '#286b69',
              bgcolor: '#dff3f2',
              fontWeight: 700,
            }}
          />
          <Typography
            component="h1"
            variant="h3"
            sx={{ color: '#22313f', fontWeight: 800, fontSize: { xs: '1.75rem', md: '2.25rem' } }}
          >
            Populi Yönetim Merkezi
          </Typography>
          <Typography sx={{ maxWidth: 680, color: '#61707d', fontSize: { xs: '0.9rem', md: '1rem' } }}>
            Kullanmak istediğiniz modülü seçerek dokümanlara, raporlara, kullanıcılara veya bot demosuna ulaşın.
          </Typography>
        </Stack>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
            gap: 2,
          }}
        >
          {visibleModules.map((module) => {
            const ModuleIcon = module.icon

            return (
              <Card
                component="article"
                key={module.path}
                variant="outlined"
                sx={{
                  display: 'flex',
                  minHeight: 210,
                  borderColor: '#dfe7ec',
                  borderRadius: 3,
                  boxShadow: '0 10px 28px rgba(39, 55, 70, 0.07)',
                  transition: 'transform 180ms ease, box-shadow 180ms ease',
                  '&:hover': {
                    transform: 'translateY(-3px)',
                    boxShadow: '0 16px 34px rgba(39, 55, 70, 0.12)',
                  },
                }}
              >
                <CardContent
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                    p: 2.5,
                    '&:last-child': { pb: 2.5 },
                  }}
                >
                  <Box
                    sx={{
                      display: 'grid',
                      placeItems: 'center',
                      width: 46,
                      height: 46,
                      mb: 1.5,
                      color: module.color,
                      bgcolor: module.background,
                      borderRadius: 2.5,
                    }}
                  >
                    <ModuleIcon sx={{ fontSize: 26 }} />
                  </Box>

                  <Typography component="h2" variant="h6" sx={{ mb: 0.5, color: '#273746', fontWeight: 750 }}>
                    {module.title}
                  </Typography>
                  <Typography sx={{ mb: 2, color: '#697985', fontSize: '0.92rem', lineHeight: 1.55 }}>
                    {module.description}
                  </Typography>

                  <Button
                    variant="contained"
                    size="small"
                    endIcon={<ArrowForward />}
                    onClick={() => navigate(module.path)}
                    sx={{
                      alignSelf: 'flex-start',
                      mt: 'auto',
                      bgcolor: module.color,
                      borderRadius: 1.5,
                      px: 1.75,
                      '&:hover': { bgcolor: module.color, filter: 'brightness(.9)' },
                    }}
                  >
                    {module.buttonLabel}
                  </Button>
                </CardContent>
              </Card>
            )
          })}
        </Box>
      </Box>
    </Box>
  )
}
